import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { toast } from "sonner";

// iter131 — Admin quick-upgrade panel (temp Phase C precursor).
// Just email + tier + duration + one button. No user search, no lookup
// UI, no bulk actions. Superseded by the full Phase C User Lookup panel
// once that ships. Route: /admin/quick-upgrade (admin-only via backend
// 403 — no frontend gate needed since non-admins see the friendly
// forbidden state below).

const DURATIONS = [
  { value: 7,   label: "7 days" },
  { value: 30,  label: "30 days" },
  { value: 90,  label: "90 days" },
  { value: 365, label: "1 year" },
  { value: 0,   label: "Permanent" },
];

const TIERS = [
  { value: "pro",   label: "Pro" },
  { value: "elite", label: "Elite" },
  { value: "free",  label: "Downgrade to Free" },
];

export default function AdminQuickUpgrade() {
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("pro");
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);       // { user, from_tier, to_tier, pro_until }
  const [error, setError] = useState(null);         // { headline, body }

  const canSubmit = email.trim().length > 3 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      const { data } = await api.post("/admin/users/upgrade-by-email", {
        email: email.trim(),
        tier,
        // If tier is free, backend ignores days; still send for shape.
        days: tier === "free" ? 0 : days,
        reason: reason.trim(),
      });
      setResult(data);
      toast.success(`${data.user.email} → ${data.to_tier.toUpperCase()}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      if (status === 403) {
        setError({ headline: "Admins only", body: "This page is admin-only. If you should have access, ask a super-admin to promote your account." });
      } else if (status === 404) {
        setError({ headline: "User not found", body: detail });
      } else {
        setError({ headline: "Couldn't complete the upgrade", body: detail || "Please try again." });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-quick-upgrade-page" className="space-y-6 max-w-2xl">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] transition-colors"
        data-testid="admin-quick-upgrade-back"
      >
        <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
      </Link>

      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-[var(--bt-accent)]" />
          <p className="bt-label">Admin · temporary</p>
        </div>
        <h1 className="font-heading text-3xl">Quick Upgrade</h1>
        <p className="text-sm text-[var(--bt-text-muted)]">
          Grant a tester Pro or Elite access by email. All actions are
          audit-logged to <code className="text-xs">set_tier_audit</code>.
        </p>
      </header>

      <section
        data-testid="admin-quick-upgrade-form"
        className="border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-5"
      >
        <div>
          <label className="bt-label block mb-1.5">User email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tester@example.com"
            className="w-full px-3 py-2 border border-[var(--bt-border)] rounded-sm bg-[var(--bt-bg)] text-sm focus:outline-none focus:border-[var(--bt-accent)]"
            data-testid="admin-quick-upgrade-email"
            autoFocus
          />
        </div>

        <div>
          <label className="bt-label block mb-1.5">Tier</label>
          <div className="flex gap-2" data-testid="admin-quick-upgrade-tier-group">
            {TIERS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTier(t.value)}
                className={`px-4 py-2 border rounded-sm text-sm transition-colors ${
                  tier === t.value
                    ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10 text-[var(--bt-text)]"
                    : "border-[var(--bt-border)] text-[var(--bt-text-muted)] hover:border-[var(--bt-text-muted)]"
                }`}
                data-testid={`admin-quick-upgrade-tier-${t.value}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tier !== "free" && (
          <div>
            <label className="bt-label block mb-1.5">Duration</label>
            <div className="flex flex-wrap gap-2" data-testid="admin-quick-upgrade-days-group">
              {DURATIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDays(d.value)}
                  className={`px-3 py-1.5 border rounded-sm text-sm transition-colors ${
                    days === d.value
                      ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10 text-[var(--bt-text)]"
                      : "border-[var(--bt-border)] text-[var(--bt-text-muted)] hover:border-[var(--bt-text-muted)]"
                  }`}
                  data-testid={`admin-quick-upgrade-days-${d.value}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="bt-label block mb-1.5">Reason (optional)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. beta tester, refund credit, comp for X"
            maxLength={500}
            className="w-full px-3 py-2 border border-[var(--bt-border)] rounded-sm bg-[var(--bt-bg)] text-sm focus:outline-none focus:border-[var(--bt-accent)]"
            data-testid="admin-quick-upgrade-reason"
          />
        </div>

        <div className="flex justify-end">
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="admin-quick-upgrade-submit"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Applying…</>
            ) : (
              <>Apply change</>
            )}
          </Button>
        </div>
      </section>

      {result && (
        <section
          data-testid="admin-quick-upgrade-result"
          className="border border-[var(--bt-win)]/40 bg-[var(--bt-win)]/5 rounded-sm p-5 space-y-2"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" style={{ color: "var(--bt-win)" }} />
            <p className="font-medium">Applied</p>
          </div>
          <dl className="text-sm space-y-1">
            <div className="flex gap-2">
              <dt className="text-[var(--bt-text-muted)] w-24">Email</dt>
              <dd data-testid="admin-quick-upgrade-result-email">{result.user.email}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-[var(--bt-text-muted)] w-24">Tier</dt>
              <dd data-testid="admin-quick-upgrade-result-tier">
                <span className="text-[var(--bt-text-muted)]">{result.from_tier}</span>
                {" → "}
                <span className="font-medium">{result.to_tier}</span>
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-[var(--bt-text-muted)] w-24">Expires</dt>
              <dd data-testid="admin-quick-upgrade-result-expires">
                {result.pro_until
                  ? new Date(result.pro_until).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                  : (result.to_tier === "free" ? "—" : "Permanent (no expiry)")}
              </dd>
            </div>
          </dl>
        </section>
      )}

      {error && (
        <section
          data-testid="admin-quick-upgrade-error"
          className="border border-[var(--bt-loss)]/40 bg-[var(--bt-loss)]/5 rounded-sm p-5 space-y-1"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" style={{ color: "var(--bt-loss)" }} />
            <p className="font-medium">{error.headline}</p>
          </div>
          <p className="text-sm text-[var(--bt-text-muted)]">{error.body}</p>
        </section>
      )}
    </div>
  );
}
